import type { Season, RateTier, RateInfo } from '../types/electricity'
import { RATES, PEAK_HOURS, SEASON_MONTHS, SEASON_NAMES } from '../data/rates'
import { getIsraelTime, getIsraelMonth, isWeekend } from './time'

export function getSeason(date: Date = new Date()): Season {
  const month = getIsraelMonth(date)
  for (const [season, months] of Object.entries(SEASON_MONTHS) as [Season, number[]][]) {
    if (months.includes(month)) return season
  }
  return 'transition'
}

export function isPeakTime(date: Date = new Date()): boolean {
  if (isWeekend(date)) return false

  const season = getSeason(date)
  const { hour } = getIsraelTime(date)
  const { start, end } = PEAK_HOURS[season]

  return hour >= start && hour < end
}

export function getCurrentRate(date: Date = new Date()): RateInfo {
  const peak = isPeakTime(date)
  const season = getSeason(date)
  const tier: RateTier = peak ? 'peak' : 'offpeak'
  const seasonRates = RATES[season]
  const rateAgorot = peak ? seasonRates.peak : seasonRates.offpeak
  const nextTier: RateTier = peak ? 'offpeak' : 'peak'

  return {
    tier,
    rateAgorot,
    rateShekel: rateAgorot / 100,
    season,
    label: peak ? 'עכשיו החשמל יקר!' : 'עכשיו החשמל זול!',
    nextChange: getNextRateChange(date),
    nextTier,
  }
}

export function getNextRateChange(date: Date = new Date()): Date {
  const now = new Date(date)
  const currentlyPeak = isPeakTime(now)

  // Check every hour boundary for the next 48 hours
  const { minute, second } = getIsraelTime(now)

  // Start from the next hour boundary
  const next = new Date(now)
  next.setMilliseconds(0)
  next.setSeconds(0)
  next.setMinutes(0)
  // Advance to next hour
  next.setTime(next.getTime() + (60 - minute) * 60 * 1000 - second * 1000)

  // Edge case: if we're at exactly an hour boundary, don't skip
  if (minute === 0 && second === 0) {
    // Check if this exact moment is a transition
    const thisHourPeak = isPeakTime(next)
    if (thisHourPeak !== currentlyPeak) return next
  }

  // Walk forward hour by hour
  for (let i = 0; i < 48; i++) {
    const checkTime = new Date(next.getTime() + i * 60 * 60 * 1000)
    if (isPeakTime(checkTime) !== currentlyPeak) {
      return checkTime
    }
  }

  // Fallback: if no change found in 48h (shouldn't happen in normal weeks)
  // Return next Monday at peak start as approximation
  const fallback = new Date(now)
  fallback.setDate(fallback.getDate() + ((8 - fallback.getDay()) % 7 || 7))
  fallback.setHours(17, 0, 0, 0)
  return fallback
}

export function getSeasonLabel(season: Season): string {
  return SEASON_NAMES[season]
}

export function formatRate(agorot: number): string {
  return (agorot / 100).toFixed(2)
}

export function calcCostPerHour(watts: number, rateAgorot: number): number {
  return (watts / 1000) * (rateAgorot / 100)
}
